import { nanoid } from 'nanoid';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Descendant } from 'slate';

import { InstructionForm } from 'src/Components/Forms/Instruction/InstructionForm';
import {
  CreateInstructionFormProvider,
  useCreateInstructionForm,
} from 'src/Hooks/Forms/useCreateInstructionForm';
import { useFetchStorageItemInformation } from 'src/Hooks/Mutations/useFetchStorageItemInformation';
import useUpdateInstruction from 'src/Hooks/Mutations/useUpdateInstruction';
import useFetchInstruction from 'src/Hooks/Queries/useFetchInstruction';

import { Props } from './InstructionEditionFormWrapper.types';

const InstructionEditionFormWrapper = ({ onCreate, onCancel, instructionId }: Props) => {
  const { data: instruction } = useFetchInstruction(instructionId);
  const { form, addSection, removeSection } = useCreateInstructionForm();
  const { mutate } = useUpdateInstruction({ onSuccess: () => onCreate() });
  const { t } = useTranslation();
  const { mutateAsync: fetchFileInfo } = useFetchStorageItemInformation();

  useEffect(() => {
    const populateAttachments = async () => {
      if (instruction) {
        form.setValues({
          title: instruction.title,
          sections: [],
        });

        if (instruction.attachment) {
          try {
            const attachmentInfo = await fetchFileInfo(instruction.attachment.id);

            form.setFieldValue('attachment', {
              fileId: attachmentInfo.id,
              name: attachmentInfo.name,
              previewUrl: attachmentInfo?.urls?.previewThumbnailUrl,
              type: attachmentInfo.type,
              video: attachmentInfo.video,
            });
          } catch (error) {
            console.error('Failed to fetch attachment information:', error);
          }
        }

        for (const section of instruction.sections) {
          const index = instruction.sections.indexOf(section);
          const attachments = section.attachments;

          try {
            const attachmentInfoPromises = attachments.map(attachment =>
              fetchFileInfo(attachment.id)
            );

            const resolvedFileInfos = await Promise.all(attachmentInfoPromises);

            const formAttachments = attachments
              .map(attachment => {
                const fileInfo = resolvedFileInfos.find(info => info.id === attachment.id);
                if (!fileInfo) return undefined;
                return {
                  fileId: attachment.id,
                  name: attachment.name,
                  previewUrl: fileInfo?.urls?.previewThumbnailUrl,
                  type: fileInfo.type,
                  video: fileInfo.video,
                };
              })
              .filter(attachment => attachment !== undefined);

            form.setFieldValue(`sections.${index}`, {
              attachments: formAttachments,
              title: section.title,
              content: section.content as Descendant[],
              id: section.id,
              localId: nanoid(),
            });
          } catch (error) {
            console.error('Failed to fetch attachment information:', error);
          }
        }
      }
    };

    populateAttachments();
  }, [instruction]);

  return (
    <CreateInstructionFormProvider form={form}>
      <InstructionForm
        onCancel={onCancel}
        form={form}
        addSection={addSection}
        removeSection={removeSection}
        onSubmit={form.onSubmit(values => {
          mutate({ instructionId, updateData: values });
        })}
        submitButtonLabel={t('editInstructionForm.editInstruction', 'Modifier')}
      />
    </CreateInstructionFormProvider>
  );
};

export default InstructionEditionFormWrapper;
